import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle, Clock, FileWarning, DollarSign } from 'lucide-react';
import { fetchPendingServiceRefunds, fetchPendingUnfiledClaims } from '../services/api';
import { NewClaimModal } from './UpsClaims';

// Parse a Neon date/timestamp (DATE renders a day early in ET via new Date()) —
// take the YYYY-MM-DD prefix and format locally.
function fmtDate(v) {
  if (!v) return '—';
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1].slice(2)}`;
  const d = new Date(s);
  return isNaN(d) ? '—' : d.toLocaleDateString();
}
const fmt = (v) => `$${(Number(v) || 0).toFixed(2)}`;

export default function PendingActivity({ navigateTo }) {
  const [tab, setTab] = useState('refunds');     // 'refunds' | 'claims'
  const [refunds, setRefunds] = useState([]);
  const [claims, setClaims] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [claimModal, setClaimModal] = useState(null);   // prefillOrder for the in-page claim modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        fetchPendingServiceRefunds(showResolved).catch(() => []),
        fetchPendingUnfiledClaims().catch(() => []),
      ]);
      setRefunds(Array.isArray(r) ? r : []);
      setClaims(Array.isArray(c) ? c : []);
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => { load(); }, [load]);

  // File a claim from an unfiled-claim row (reuses the Claims modal in-page).
  const fileClaim = (row) => {
    setClaimModal({
      order_id: row.order_id || '',
      bc_order_id: row.bc_order_id || '',
      tracking_number: row.tracking_number || '',
      customer_name: row.customer || '',
      receiver_name: row.customer || '',
      receiver_state: row.state || '',
      selected_delivery: row.scheduled_date || '',
      actual_delivery: row.delivered_at || '',
      // Real ship date (orders.shipdate) — NOT order_date. They differ on ~73% of
      // orders (shipped a day+ after ordering), which was seeding claims a day early.
      ship_date: row.ship_date || row.order_date || '',
      shipping_cost: row.refund_amount || '',
    });
  };

  const waiting = refunds.filter(r => !r.resolved).length;
  const urgent = claims.filter(c => c.days_left != null && c.days_left >= 0 && c.days_left <= 7).length;

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)' }}>Pending Activity</div>
        <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 2 }}>
          Service-refund credits we're waiting on, and late-delivery claims still to be filed before the 45-day window closes.
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <button onClick={() => setTab('refunds')} className={tab === 'refunds' ? 'btn btn-primary' : 'btn btn-outline'}>
          <DollarSign size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Service Refunds {waiting > 0 && <span style={{ marginLeft: 4, fontWeight: 700 }}>({waiting})</span>}
        </button>
        <button onClick={() => setTab('claims')} className={tab === 'claims' ? 'btn btn-primary' : 'btn btn-outline'}>
          <FileWarning size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Unfiled Claims {urgent > 0 && <span style={{ marginLeft: 4, fontWeight: 700, color: tab === 'claims' ? '#fff' : 'var(--orange)' }}>({urgent} urgent)</span>}
        </button>
        <button onClick={load} className="btn btn-ghost" style={{ marginLeft: 'auto' }} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'spin' : undefined} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)' }}>Loading…</div>
      ) : tab === 'refunds' ? (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>Service Refunds — <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>same-day late refunds marked Credited; "resolved" = the GSR credit landed on a later bill.</span></span>
            <label style={{ marginLeft: 'auto', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)} />
              Show resolved
            </label>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {refunds.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)' }}>
                Nothing waiting — all credited refunds have landed.
              </div>
            ) : (
              <table className="orders-table" style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Order</th><th>Customer</th><th>Tracking</th>
                    <th>Order Date</th><th>Credited</th>
                    <th style={{ textAlign: 'right' }}>Refund</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {refunds.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>
                        {r.bc_order_id ? <a onClick={() => navigateTo && navigateTo('order-details', r.bc_order_id)} style={{ color: 'var(--orange)', cursor: 'pointer' }}>#{r.bc_order_id}</a> : '—'}
                      </td>
                      <td>{r.customer || '—'}{r.state ? <span style={{ color: 'var(--gray-400)' }}> · {r.state}</span> : null}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.tracking_number}</td>
                      <td>{fmtDate(r.order_date)}</td>
                      <td>{fmtDate(r.credited_at)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(r.refund_amount)}</td>
                      <td>
                        {r.resolved ? (
                          <span style={{ color: '#1a8a4a', fontWeight: 600 }}>
                            <CheckCircle size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                            Resolved{r.credited_on_invoice ? ` · ${r.credited_on_invoice}` : ''}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--orange)', fontWeight: 600 }}>
                            <Clock size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />Waiting for credit
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            Unfiled Claims — <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>whole-day late packages with no claim filed yet. Must be filed within 45 days of delivery.</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {claims.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)' }}>No unfiled claims — everything's filed.</div>
            ) : (
              <table className="orders-table" style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Order</th><th>Customer</th><th>Tracking</th>
                    <th>Order Date</th><th>Delivered</th><th>Expires</th><th style={{ textAlign: 'center' }}>Days Left</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((c, i) => {
                    const dl = c.days_left;
                    const expired = dl != null && dl < 0;
                    const urgentRow = dl != null && dl >= 0 && dl <= 7;
                    const color = expired ? 'var(--red)' : urgentRow ? 'var(--orange)' : 'var(--navy)';
                    return (
                      <tr key={i} style={expired ? { background: '#fdf3f3' } : urgentRow ? { background: '#fff7ed' } : undefined}>
                        <td style={{ fontWeight: 600 }}>
                          {c.bc_order_id ? <a onClick={() => navigateTo && navigateTo('order-details', c.bc_order_id)} style={{ color: 'var(--orange)', cursor: 'pointer' }}>#{c.bc_order_id}</a> : '—'}
                        </td>
                        <td>{c.customer || '—'}{c.state ? <span style={{ color: 'var(--gray-400)' }}> · {c.state}</span> : null}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{c.tracking_number}</td>
                        <td>{fmtDate(c.order_date)}</td>
                        <td>{fmtDate(c.delivered_at)}</td>
                        <td>{fmtDate(c.expiry_date)}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color }}>
                          {dl == null ? '—' : expired ? `expired ${-dl}d ago` : `${dl}d`}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button className="btn btn-primary" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => fileClaim(c)}>File Claim</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {claimModal && (
        <NewClaimModal
          prefillOrder={claimModal}
          onClose={() => setClaimModal(null)}
          onSubmitted={() => { setClaimModal(null); load(); }}
        />
      )}
    </div>
  );
}
